import { useMemo, type ReactNode } from 'react';
import type { TaskBodyProps } from '../index';
import { DesignPlanHistory } from '../DesignPlanHistory';
import { DxTabLabel } from '../DxTabLabel';
import { PatientExternalLinks } from '../PatientExternalLinks';
import { TaskSidebarPanel } from '../TaskSidebarPanel';
import { buildSidebarTabs, designLoopInfoTabs } from '../taskSidebarTabs';
import { DxReportLandingPage } from '../../../patient-files/actions/dx-report/DxReportLandingPage';
import { useInvalidateDxReportDrafts } from '../../../../hooks/useDxReportDrafts';
import type { ActiveHandBack } from '../../../../services/finalReview';
import { CountTabLabel } from '../CountTabLabel';
import { HandBackFeedbackCard } from './HandBackBanner';

// TaskSurface owns the back button.
const noBack = () => {};

interface PastSubmissionsProps {
    patientId: string;
    submitTypes: string[];
    title?: string;
    headerEnd?: ReactNode;
    handBack?: ActiveHandBack | null;
}

export function PastSubmissions({
    patientId,
    submitTypes,
    title = 'Past submissions',
    headerEnd,
    handBack = null
}: PastSubmissionsProps) {
    return (
        <DesignPlanHistory
            flush
            patientId={patientId}
            submitType={submitTypes}
            title={title}
            emptyText="No previous submissions for this case yet."
            headerRight={<PatientExternalLinks patientId={patientId} manufacturerOnly />}
            headerEnd={headerEnd}
            bodyStart={
                <HandBackFeedbackCard handBack={handBack} section="design" className="mb-3" />
            }
        />
    );
}

type DesignDxTaskLayoutProps = Pick<
    TaskBodyProps,
    'workCase' | 'statusMeta' | 'onRegisterBeforeClose'
> & {
    // Memoize it in the caller, or every mounted tab re-renders with the body.
    submissions: ReactNode;
    /** The Final review notes this step has to address, shown and counted on their tabs. */
    handBack?: ActiveHandBack | null;
    revisionsHeaderAction?: ReactNode;
};

// The Dx wizard's last step only saves the draft; the case moves only through the footer actions.
export function DesignDxTaskLayout({
    workCase: c,
    statusMeta,
    submissions,
    onRegisterBeforeClose,
    handBack = null,
    revisionsHeaderAction
}: DesignDxTaskLayoutProps) {
    const refreshDxReport = useInvalidateDxReportDrafts(c.id);

    const notes = handBack?.notes ?? [];
    const designNoteCount = notes.filter((n) => n.section === 'design').length;
    const dxNoteCount = notes.length - designNoteCount;

    const tabs = useMemo(
        () => [
            {
                key: 'submissions',
                label: (
                    <CountTabLabel label="Submissions" count={designNoteCount} tone="attention" />
                ),
                content: submissions
            },
            {
                key: 'dx-report',
                label: <DxTabLabel patientId={c.id} noteCount={dxNoteCount} />,
                content: (
                    <>
                        <HandBackFeedbackCard
                            handBack={handBack}
                            section="dx"
                            className="mx-5 mt-3 flex-shrink-0"
                        />
                        <DxReportLandingPage
                            patientId={c.id}
                            patientName={c.name}
                            mode="author"
                            onBack={noBack}
                            onDataChange={refreshDxReport}
                            onRegisterBeforeClose={onRegisterBeforeClose}
                        />
                    </>
                )
            },
            ...buildSidebarTabs(c.id, designLoopInfoTabs(statusMeta.macro), null, {
                revisionsHeaderAction
            }).map((t) => ({ ...t, trailing: true }))
        ],
        [
            c.id,
            c.name,
            statusMeta.macro,
            revisionsHeaderAction,
            submissions,
            onRegisterBeforeClose,
            refreshDxReport,
            handBack,
            designNoteCount,
            dxNoteCount
        ]
    );

    return <TaskSidebarPanel tabs={tabs} keepMounted flush />;
}
